import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { formatPrice, planInclusions } from "../planFormat";
import {
  PLAN_STATUS_PILL,
  PLAN_STATUS_STYLES,
} from "../planStatusStyles";
import { BrandPlan } from "../types/brand.types";
import SupportChannelList from "./SupportChannelList";

const COLUMNS = [
  "Plan name",
  "Duration",
  "Price",
  "Support",
  "Created on",
  "Status",
];

const bodyCell = "px-4 sm:px-6 py-4 text-[14px] text-ink";

interface PlansTableProps {
  plans: BrandPlan[];
  /** Stands in for the rows when the list comes back with none. */
  empty: EmptyStateProps;
  /**
   * Where the list is. The body says nothing while it is on its way, and only
   * a wait long enough to notice shimmers, as one block the size of the table.
   * Rows already in hand are left alone either way: a table with something to
   * show goes on showing it while the next answer is fetched.
   */
  phase?: LoadPhase;
  onEdit: (plan: BrandPlan) => void;
  /** Asks to list the plan on the public page, or to take it off again. */
  onToggleStatus: (plan: BrandPlan) => void;
}

/**
 * The plans list from `md` up, where there is room for its six columns.
 * Below that the same rows are laid out as cards — see `PlanCards`.
 */
const PlansTable: React.FC<PlansTableProps> = ({
  plans,
  empty,
  phase = "ready",
  onEdit,
  onToggleStatus,
}) =>
  phase !== "ready" && plans.length === 0 ? (
    <div className="flex-1 min-h-0 p-4 sm:p-6">
      <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
    </div>
  ) : (
  // The only scroll region on the tab: rows move, the sticky header row and
  // the card's pagination footer stay put. `min-h-0` is what lets this shrink
  // inside the card's flex column instead of pushing the footer off-screen.
  <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
    {/* Full height only when the rows are gone — see `ClientsTable`. */}
    <table
      className={`w-full table-fixed border-collapse text-left ${
        // The width the seven columns need is only worth insisting on when
        // there are rows to lay out in them. With none, the min-width would
        // push the empty state out past a phone’s edge and leave the user
        // scrolling sideways to read why the list is empty — so the table
        // takes the scroll region as it finds it and the state centres in it.
        plans.length === 0 ? "h-full" : "min-w-[880px]"
      }`}
    >
      <colgroup>
        <col className="w-[26%]" />
        <col className="w-[13%]" />
        <col className="w-[13%]" />
        <col className="w-[12%]" />
        <col className="w-[15%]" />
        <col className="w-[14%]" />
        <col className="w-[7%]" />
      </colgroup>
      {/* The header pins to the top of this container. Its bottom rule is an
          inset shadow rather than a border because collapsed table borders
          scroll away from a sticky cell. */}
      <thead className="sticky top-0 z-10">
        <tr>
          {COLUMNS.map((heading) => (
            <th
              key={heading}
              scope="col"
              className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
            >
              {heading}
            </th>
          ))}
          <th
            scope="col"
            className="bg-white px-4 sm:px-6 py-3 shadow-[inset_0_-1px_0_#E9EDF1]"
          >
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      {plans.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={COLUMNS.length + 1} className="align-middle">
              <EmptyState {...empty} />
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {plans.map((plan) => (
            <tr
              key={plan.id}
              className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors"
            >
              <td className="px-4 sm:px-6 py-4">
                <p className="truncate text-[14px] text-ink">{plan.name}</p>
                <PlanInclusions plan={plan} />
              </td>
              <td className={bodyCell}>{plan.duration}</td>
              <td className={bodyCell}>{formatPrice(plan.price)}</td>
              <td className="px-4 sm:px-6 py-4">
                <SupportChannelList channels={plan.supportChannels} />
              </td>
              <td className={bodyCell}>{plan.createdOn}</td>
              <td className="px-4 sm:px-6 py-4">
                <span
                  className={`${PLAN_STATUS_PILL} ${
                    PLAN_STATUS_STYLES[plan.status]
                  }`}
                >
                  {plan.status}
                </span>
              </td>
              <td className="px-4 sm:px-6 py-4">
                <RowActionsMenu
                  // The whole row identifies the plan: the same name and
                  // duration can appear twice at different prices, so the
                  // price is what makes the label unambiguous.
                  label={`Actions for ${plan.name}, ${
                    plan.duration
                  }, ${formatPrice(plan.price)}`}
                  // Delete is not offered: there is no endpoint behind it, and
                  // deactivating is what takes a plan off the public page.
                  actions={[
                    { label: "Edit", onSelect: () => onEdit(plan) },
                    {
                      label:
                        plan.status === "Active" ? "Deactivate" : "Activate",
                      destructive: plan.status === "Active",
                      onSelect: () => onToggleStatus(plan),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      )}
    </table>
  </div>
  );

/**
 * What the plan includes, under its name: the counts it puts a number on,
 * separated by bars. Nothing at all where it answers none of them, rather than
 * an empty line holding the rows below it apart.
 */
const PlanInclusions: React.FC<{ plan: BrandPlan }> = ({ plan }) => {
  const parts = planInclusions(plan);
  if (parts.length === 0) return null;

  return (
    <p className="mt-1 truncate text-[12px] text-ink-muted">
      {parts.join(" | ")}
    </p>
  );
};

export default PlansTable;
